// The signature editor's client-side rules, from
// `extension-alert-candidates-message-content.component.ng1.ts` (upstream woo@10e2fe0302 and
// the three follow-ups merged with it at woo@211e1c02fe). HubSpot 49214431098: a click-away
// autosave posted an editor that was still EMPTY over a stored 76KB signature.
//
// Upstream's `saveSignature()` validates, closes an unchanged-empty editor, confirms a clear,
// POSTs `{ signature, clear }`, and then decides whether the editor may close. The decisions are
// here (`planSignatureSave`, `afterSignatureSave`); the confirmation and the POST are the
// composer's, because one is a dialogue and the other is the host's call.
import { checkSignatureParameterValidity } from './messageParamValidation.ts'

/** `SignatureLoadState`. The editor is locked unless this is `'loaded'`. */
export type SignatureLoadState = 'loading' | 'loaded' | 'failed'

/**
 * `getInvalidSignatureParametersMessage` — upstream's inline error, verbatim. Null when there
 * is nothing to say.
 */
export function invalidSignatureParametersMessage(invalid: readonly string[]): string | null {
  if (invalid.length === 0) return null
  const tokens = invalid.map((param) => `{${param}}`).join(', ')
  return `Unknown parameters in signature: ${tokens}. Supported: {First name}, {Hunter name}.`
}

/** The slice of a parsed document `isEmptySignature` reads — injectable for a test without a DOM. */
export interface ParsedSignature {
  body: { textContent: string | null; querySelector: (selector: string) => unknown }
}

function parseHtml(html: string): ParsedSignature {
  // DOMParser, unlike innerHTML on a live element, never loads images or runs handlers in the
  // markup — upstream's own note, and the reason it is not a detached `<div>`.
  return new DOMParser().parseFromString(html, 'text/html')
}

/**
 * `isEmptySignature` — blank editor markup such as `<p><br></p>` or a lone `&nbsp;` counts as
 * empty; an image-only signature does NOT, because a logo is a signature.
 */
export function isEmptySignature(
  signature: string | null | undefined,
  parse: (html: string) => ParsedSignature = parseHtml,
): boolean {
  if (signature === null || signature === undefined || signature === '') return true
  const parsed = parse(signature)
  return (
    (parsed.body.textContent ?? '').replace(/ /g, ' ').trim() === '' &&
    parsed.body.querySelector('img') === null
  )
}

/**
 * `isSignatureDirty` — open, and different from what was last loaded or saved. Null and the
 * empty string are the same signature: the server answers `null` for one never set, and the
 * editor reports `''` once touched.
 */
export function isSignatureDirty(
  isEditing: boolean,
  signature: string | null | undefined,
  savedSignature: string | null | undefined,
): boolean {
  return isEditing && (signature ?? '') !== (savedSignature ?? '')
}

export type SignatureSavePlan =
  /** Unknown tokens: stay open and show `message`. Nothing is written. */
  | { kind: 'invalid'; invalid: string[]; message: string }
  /** Empty, and the saved one was empty too: close the editor. Nothing to write. */
  | { kind: 'close' }
  /** POST now — after the "Remove your signature?" confirmation when `clear`. */
  | { kind: 'write'; clear: boolean }

/** Everything `saveSignature()` decides before the POST, in upstream's order. */
export function planSignatureSave(
  signature: string | null | undefined,
  savedSignature: string | null | undefined,
  parse?: (html: string) => ParsedSignature,
): SignatureSavePlan {
  const invalid = checkSignatureParameterValidity(signature)
  const message = invalidSignatureParametersMessage(invalid)
  if (message !== null) return { kind: 'invalid', invalid, message }
  const clear = isEmptySignature(signature, parse)
  if (clear && isEmptySignature(savedSignature, parse)) return { kind: 'close' }
  return { kind: 'write', clear }
}

export type SignatureAfterSave =
  /** Close the editor showing `saved`. */
  | { kind: 'close'; saved: string }
  /**
   * Edits were typed while the request was in flight and were NOT sent: keep the editor open,
   * measure "unsaved" against what the server now holds.
   */
  | { kind: 'keep-open'; saved: string }

/**
 * The tail of `saveSignature()` after a successful POST (woo@30b5a7d45c). `submitted` is the
 * editor's text when SAVE was pressed and `current` is its text now; a clear stores nothing, so
 * the saved value is empty rather than the blank markup that was in the editor.
 */
export function afterSignatureSave(submitted: string, current: string, clear: boolean): SignatureAfterSave {
  const saved = clear ? '' : submitted
  return current !== submitted ? { kind: 'keep-open', saved } : { kind: 'close', saved }
}

/**
 * The guard at the top of `onSignatureChange` (woo@c7a5e20f87): an editor update that arrives
 * while the editor is CLOSED is dropped, so a keystroke committed after CANCEL cannot resurrect
 * the text CANCEL discarded. One that arrives during a save is kept — that is the in-flight edit
 * `afterSignatureSave` exists to notice.
 *
 * Upstream needs it because textAngular commits keystrokes on a debounced `$timeout` that
 * read-only mode does not cancel. `RichTextEditor` reports synchronously on `input` and a closed
 * editor is not contenteditable, so the race has no known path here; the guard is ported anyway
 * so the composer refuses the same states upstream's does. The other half of upstream's fix —
 * putting the saved value back in the view — is `RichTextEditor`'s, which reverts any write to
 * a disabled editor.
 */
export function acceptsSignatureChange(isEditing: boolean, isSaving: boolean): boolean {
  return isEditing || isSaving
}

/** Upstream's save-failure message, verbatim. */
export const SIGNATURE_SAVE_FAILED_MESSAGE = "Couldn't save your signature. Please try again."
